import { UserRole } from './roles';
import { ApplicationError } from './error.types';

export enum AuthErrorType {
  INVALID_CREDENTIALS = 'INVALID_CREDENTIALS',
  NETWORK_ERROR = 'NETWORK_ERROR',
  SESSION_EXPIRED = 'SESSION_EXPIRED',
  REFRESH_FAILED = 'REFRESH_FAILED',
  AUTHENTICATION = 'AUTHENTICATION',
  NETWORK = 'NETWORK',
  SERVER = 'SERVER',
  UNKNOWN = 'UNKNOWN'
}

export interface AuthError {
  type: AuthErrorType;
  message: string;
  code?: string;
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
}

export interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  tokens: AuthTokens | null;
  loading: {
    login: boolean;
    logout: boolean;
    refresh: boolean;
  };
  error: AuthError | null;
}

export interface AuthContextType extends AuthState {
  login: (identifier: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshAccessToken: () => Promise<boolean>;
  loginWithToken: (tokens: string) => Promise<void>;
  signup: (data: {
    name: string;
    email?: string;
    whatsappPhone?: string;
    password: string;
  }) => Promise<void>;
  isLoading: boolean;
}

export class AuthServiceError extends ApplicationError {
  readonly type: AuthErrorType;

  constructor(type: AuthErrorType, message?: string) {
    const errorMessage = message || AuthServiceError.getDefaultMessage(type);
    super('AuthServiceError', errorMessage, 401);
    this.type = type;
    this.name = 'AuthServiceError';
  }

  static getDefaultMessage(type: AuthErrorType): string {
    switch (type) {
      case AuthErrorType.INVALID_CREDENTIALS:
        return 'Invalid credentials provided';
      case AuthErrorType.SESSION_EXPIRED:
        return 'Your session has expired';
      case AuthErrorType.NETWORK_ERROR:
        return 'Network error occurred';
      case AuthErrorType.REFRESH_FAILED:
        return 'Failed to refresh authentication';
      case AuthErrorType.AUTHENTICATION:
        return 'Authentication error occurred';
      case AuthErrorType.NETWORK:
        return 'Network error occurred';
      case AuthErrorType.SERVER:
        return 'Server error occurred';
      default:
        return 'An unknown error occurred';
    }
  }
}

export interface LoginCredentials {
  identifier: string;
  password: string;
}

export interface AuthResponse {
  access_token?: string;
  refresh_token?: string;
  csrf_token?: string;
  user: {
    id: number;
    email?: string | null;
    whatsappNumber?: string | null;
    firstName: string;
    lastName: string;
    role: string;
  };
}

export interface User {
  id: string;
  email: string | null;
  whatsappNumber: string | null;
  firstName: string;
  lastName: string;
  role: UserRole;
  isAuthenticated: boolean;
}

export interface LoginResponse {
  access_token?: string;
  refresh_token?: string;
  csrf_token?: string;
  user: {
    id: number;
    email?: string | null;
    whatsappNumber?: string | null;
    firstName: string;
    lastName: string;
    role: string;
  };
}
